import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useFriends } from './useFriends';

export interface ChatContact {
  id: string;
  name: string;
  avatar_url?: string;
  type: 'friend' | 'organizer';
  subtitle?: string;
}

export function useContactsForChat() {
  const { user } = useAuth();
  const { friends, following, loading: friendsLoading } = useFriends();
  const [organizers, setOrganizers] = useState<ChatContact[]>([]);
  const [loadingOrganizers, setLoadingOrganizers] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch organizers from events the user is registered to
  useEffect(() => {
    const fetchOrganizers = async () => {
      if (!user) {
        setLoadingOrganizers(false);
        return;
      }

      try {
        setLoadingOrganizers(true);

        // 1. Get events where the user is registered
        const { data: registrations, error: regError } = await supabase
          .from('registrations')
          .select(`
            event_id,
            events!inner (
              id,
              title,
              organizer_id
            )
          `)
          .or(`user_id.eq.${user.id},partner_user_id.eq.${user.id}`)
          .eq('status', 'confirmed');

        if (regError) throw regError;

        if (!registrations || registrations.length === 0) {
          setOrganizers([]);
          setLoadingOrganizers(false);
          return;
        }

        // 2. Extract unique organizer_ids with their event titles
        const organizerMap = new Map<string, { userId: string; eventTitles: string[] }>();
        
        for (const reg of registrations) {
          const event = reg.events as any;
          if (event && event.organizer_id) {
            const existing = organizerMap.get(event.organizer_id);
            if (existing) {
              if (!existing.eventTitles.includes(event.title)) {
                existing.eventTitles.push(event.title);
              }
            } else {
              organizerMap.set(event.organizer_id, {
                userId: event.organizer_id,
                eventTitles: [event.title]
              });
            }
          }
        }

        // Filter out the current user from organizers
        organizerMap.delete(user.id);

        if (organizerMap.size === 0) {
          setOrganizers([]);
          setLoadingOrganizers(false);
          return;
        }

        const organizerUserIds = Array.from(organizerMap.keys());

        // 3. Get organizer profiles - try organizer_public_profiles first, fallback to profiles
        const { data: orgProfiles, error: orgError } = await supabase
          .from('organizer_public_profiles')
          .select('user_id, company_name, avatar_url')
          .in('user_id', organizerUserIds);

        if (orgError) {
          console.error('Error fetching organizer profiles:', orgError);
        }

        // Also fetch from profiles for any missing
        const { data: profilesData, error: profilesError } = await supabase
          .from('profiles')
          .select('user_id, full_name, avatar_url')
          .in('user_id', organizerUserIds);

        if (profilesError) {
          console.error('Error fetching profiles:', profilesError);
        }

        // 4. Build organizer contacts
        const organizerContacts: ChatContact[] = [];

        for (const [userId, info] of organizerMap) {
          const orgProfile = orgProfiles?.find(p => p.user_id === userId);
          const profile = profilesData?.find(p => p.user_id === userId);
          
          const name = orgProfile?.company_name || profile?.full_name || 'Organizador';
          const avatarUrl = orgProfile?.avatar_url || profile?.avatar_url;
          const eventText = info.eventTitles.length === 1 
            ? info.eventTitles[0] 
            : `${info.eventTitles.length} eventos`;

          organizerContacts.push({
            id: userId,
            name,
            avatar_url: avatarUrl || undefined,
            type: 'organizer',
            subtitle: `Organizador · ${eventText}`
          });
        }

        setOrganizers(organizerContacts);
      } catch (err) {
        console.error('Error fetching organizers for chat:', err);
        setError(err instanceof Error ? err.message : 'Error fetching organizers');
      } finally {
        setLoadingOrganizers(false);
      }
    };

    fetchOrganizers();
  }, [user]);

  // Combine friends from following list
  const friendContacts = useMemo((): ChatContact[] => {
    if (!user) return [];
    
    // Use 'following' which contains people the user follows
    const allFollowing = following || friends || [];
    
    return allFollowing
      .filter(f => f.friend_profile?.user_id && f.friend_profile.user_id !== user.id)
      .map(f => ({
        id: f.friend_profile.user_id,
        name: f.friend_profile.full_name || 'Usuario',
        avatar_url: f.friend_profile.avatar_url || undefined,
        type: 'friend' as const,
        subtitle: 'Amigo'
      }));
  }, [following, friends, user]);

  // Combine and deduplicate contacts (organizers that are also friends only appear once as friends)
  const contacts = useMemo((): ChatContact[] => {
    const friendIds = new Set(friendContacts.map(f => f.id));
    
    // Filter out organizers that are already in friends list
    const uniqueOrganizers = organizers.filter(org => !friendIds.has(org.id));
    
    return [...friendContacts, ...uniqueOrganizers];
  }, [friendContacts, organizers]);

  // Search function
  const searchContacts = (query: string): { friends: ChatContact[]; organizers: ChatContact[] } => {
    const normalizedQuery = query.toLowerCase().trim();
    
    if (!normalizedQuery) {
      const friendIds = new Set(friendContacts.map(f => f.id));
      const uniqueOrganizers = organizers.filter(org => !friendIds.has(org.id));
      return { friends: friendContacts, organizers: uniqueOrganizers };
    }
    
    const filteredFriends = friendContacts.filter(c => 
      c.name.toLowerCase().includes(normalizedQuery)
    );
    
    const friendIds = new Set(friendContacts.map(f => f.id));
    const filteredOrganizers = organizers
      .filter(org => !friendIds.has(org.id))
      .filter(c => 
        c.name.toLowerCase().includes(normalizedQuery) ||
        c.subtitle?.toLowerCase().includes(normalizedQuery)
      );
    
    return { friends: filteredFriends, organizers: filteredOrganizers };
  };

  return {
    contacts,
    friendContacts,
    organizerContacts: organizers.filter(org => !new Set(friendContacts.map(f => f.id)).has(org.id)),
    loading: friendsLoading || loadingOrganizers,
    error,
    searchContacts
  };
}
